import type { FC } from 'react';
import React from 'react';
import { useTranslation } from '@shared/core';
import { addRendition } from '@shared/utils/photoRendition';
import { backgroundImageForUrl } from '@shared/utils/style/backgroundImage';
import { Box, ButtonV2 } from '@withjoy/joykit';
import { Camera } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import type { UploadedPreviewImages } from '@shared/components/PreviewWithPhoto/PreviewWithPhoto.types';

const PHOTO_SIZE = 28;
const STACK_OFFSET = 5;

type UseYourPhotoButtonProps = Readonly<{
  uploadedPreviewImages: UploadedPreviewImages;
  onClick: () => void;
}>;

/**
 * Gallery-only fork of the shared `CustomPhotoPreviewButton`: a standard muted (secondary) button
 * sized and filled (mono2) to match the filter pills, instead of the AI-gradient pill the Website Designer keeps.
 */
export const UseYourPhotoButton: FC<UseYourPhotoButtonProps> = ({ uploadedPreviewImages, onClick }) => {
  const { tq } = useTranslation('stationery');

  const hasPhotos = uploadedPreviewImages.length > 0;
  const visiblePhotos = uploadedPreviewImages.slice(0, 3);
  const label = !hasPhotos ? tq`Use Your Photo` : uploadedPreviewImages.length > 1 ? tq`Change Your Photos` : tq`Change Your Photo`;

  const startIcon = hasPhotos ? (
    <Box
      position="relative"
      width={pxToRem(PHOTO_SIZE + (visiblePhotos.length - 1) * STACK_OFFSET)}
      height={pxToRem(PHOTO_SIZE)}
      flexShrink={0}
    >
      {visiblePhotos.map((photo, index) => (
        <Box
          key={photo.id}
          position="absolute"
          top={0}
          left={pxToRem(index * STACK_OFFSET)}
          zIndex={visiblePhotos.length - index}
          size={pxToRem(PHOTO_SIZE)}
          borderRadius="full"
          overflow="hidden"
          border="1px solid"
          borderColor="white"
          backgroundColor="mono3"
          backgroundImage={backgroundImageForUrl(addRendition({ url: photo.url, renditionSize: 'medium' }))}
          backgroundRepeat="no-repeat"
          backgroundPosition="center"
          backgroundSize="cover"
        />
      ))}
    </Box>
  ) : (
    <Camera size={20} />
  );

  return (
    <ButtonV2
      variant="muted"
      shape="rounded"
      // Lighter than the muted default (mono3) to match the neutral filter pills beside it; mono3,
      // the muted fill, becomes the hover so the button still darkens rather than lightens.
      backgroundColor="mono2"
      _hover={{ backgroundColor: 'mono3' }}
      height={pxToRem(48)}
      startIcon={startIcon}
      onClick={onClick}
      whiteSpace="nowrap"
    >
      {label}
    </ButtonV2>
  );
};

UseYourPhotoButton.displayName = 'UseYourPhotoButton';
